import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, AlertTriangle, AlertOctagon, Info, CheckCheck, Clock, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { alerts as alertsApi } from '@/lib/api';

const POLL_MS = 30000;

const SEV_ICON = {
  critical: AlertOctagon,
  warning: AlertTriangle,
  info: Info,
};

const SEV_STYLE = {
  critical: 'text-red-600 bg-red-50 border-red-200',
  warning: 'text-amber-600 bg-amber-50 border-amber-200',
  info: 'text-sky-600 bg-sky-50 border-sky-200',
};

export function AlertsBell() {
  const nav = useNavigate();
  const [items, setItems] = React.useState([]);
  const [unread, setUnread] = React.useState(0);
  const [open, setOpen] = React.useState(false);

  const load = React.useCallback(() => {
    alertsApi.list({ limit: 20 }).then((r) => { setItems(r.items || []); setUnread(r.unread || 0); }).catch(() => {});
  }, []);

  React.useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  const markRead = async (id) => {
    try { await alertsApi.markRead(id); load(); } catch { /* noop */ }
  };
  const markAllRead = async () => {
    try { await alertsApi.markAllRead(); load(); } catch { /* noop */ }
  };

  const targetFor = (a) => {
    // Prefer explicit deep-link on payload, else derive from kind + matter_id.
    const link = a?.payload?.link;
    if (link) return link;
    const mid = a?.payload?.matter_id;
    if (a.kind === 'trust_low_balance') return '/app/trust';
    if (!mid) return null;
    const tab = a.kind === 'document_declaration' ? '?tab=docs' : '';
    return `/app/matters/${mid}${tab}`;
  };

  const openAlert = async (a) => {
    if (!a.read_at) await alertsApi.markRead(a.id).catch(() => {});
    const to = targetFor(a);
    if (to) { setOpen(false); nav(to); }
    else { load(); }
  };

  const snoozeAlert = async (e, a, hours) => {
    e.stopPropagation();
    try { await alertsApi.snooze(a.id, hours); load(); } catch { /* noop */ }
  };

  // Hours until 9am tomorrow in the user's local timezone. Backend caps at 168h.
  const hoursUntilTomorrow9am = () => {
    const now = new Date();
    const t = new Date(now);
    t.setDate(t.getDate() + 1);
    t.setHours(9, 0, 0, 0);
    return Math.max(0.5, Math.min(168, (t - now) / 36e5));
  };

  const SNOOZE_PRESETS = [
    { key: '1h', label: '1 hour', hours: 1 },
    { key: '4h', label: '4 hours', hours: 4 },
    { key: '9am', label: 'Until tomorrow 9am', hours: null }, // computed on click
    { key: '24h', label: '24 hours', hours: 24 },
    { key: '7d', label: '7 days', hours: 168 },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="alerts-bell"
          className="relative rounded-full p-2 text-slate-600 hover:bg-slate-100"
          aria-label={`${unread} unread alerts`}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="alerts-bell-count"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="alerts-bell-popover">
        <div className="flex items-center justify-between border-b border-slate-100 p-3">
          <div className="text-sm font-semibold text-[#1A3A5C]">Firm alerts</div>
          {unread > 0 && (
            <Button data-testid="alerts-mark-all" variant="ghost" size="sm" onClick={markAllRead} className="h-7 gap-1 text-xs text-slate-500 hover:text-[#1A3A5C]">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400">No alerts. Everything looks quiet.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="alerts-bell-items">
              {items.map((a) => {
                const Ico = SEV_ICON[a.severity] || Info;
                const tone = SEV_STYLE[a.severity] || SEV_STYLE.info;
                const readCls = a.read_at ? 'opacity-60' : '';
                const hasLink = !!targetFor(a);
                return (
                  <li
                    key={a.id}
                    data-testid={`alert-item-${a.id}`}
                    data-has-link={hasLink ? 'true' : 'false'}
                    onClick={() => openAlert(a)}
                    className={`flex cursor-pointer items-start gap-3 p-3 hover:bg-slate-50 ${readCls}`}
                  >
                    <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${tone}`}>
                      <Ico className="h-3 w-3" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-[#1A3A5C]">{a.title}</div>
                      <div className="mt-0.5 whitespace-pre-line text-[11px] text-slate-600 line-clamp-3">{a.body}</div>
                      <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-400">
                        <span>{new Date(a.created_at).toLocaleString('en-AU')}</span>
                        {a.matter_ref && <span>· {a.matter_ref}</span>}
                        {!a.read_at && <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] text-white">NEW</span>}
                        <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                data-testid={`alert-snooze-${a.id}`}
                                className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] text-slate-500 hover:bg-slate-100 hover:text-[#1A3A5C]"
                                title="Snooze this alert"
                              >
                                <Clock className="h-2.5 w-2.5" /> Snooze <ChevronDown className="h-2.5 w-2.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[180px]" data-testid={`alert-snooze-menu-${a.id}`}>
                              {SNOOZE_PRESETS.map((p) => (
                                <DropdownMenuItem
                                  key={p.key}
                                  data-testid={`alert-snooze-${p.key}-${a.id}`}
                                  onClick={(e) => snoozeAlert(e, a, p.key === '9am' ? hoursUntilTomorrow9am() : p.hours)}
                                  className="cursor-pointer text-xs"
                                >
                                  <Clock className="mr-2 h-3 w-3 text-slate-400" /> {p.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
